import {
  Link,
  Text,
} from '@chakra-ui/react';
import Chart from 'react-apexcharts';
import { selectedDatabaseIdState, currentDatabaseSelector } from '@transport-insights/uikit';
import {
  useDQAssetManagement,
} from '@src/modules/performance/context/reports-api-hooks';
import { useRecoilValue } from 'recoil';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import { filterChartDataByName } from '@src/modules/performance/shared/helpers';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Annual trend for Score',
  content: [
    {
      title: 'Data Quality scores',
      description:
  <>
    <Text fontSize="inherit" mb={2}>
      Each RCA&apos;s annual overall data quality score is a weighted percentage based on the level of importance of each metric and the grades that are achieved for the year.
    </Text>
    <Text fontSize="inherit" mb={2}>
      Data quality metric criteria change every year as the framework is constantly being refined. This means the results for a year are not directly comparable to the results for previous years which are based on different metrics and criteria.
    </Text>
    <Text fontSize="inherit" mb={2}>
      To allow for a more fair comparison against historical results, previous years&apos; results are recalculated based on the metric criteria for the most recent financial year.
    </Text>
    <Text fontSize="inherit" mb={2}>
      Further reference:
      <br />
      <Link href="https://www.nzta.govt.nz/assets/Road-Efficiency-Group/docs/practice-overviews/REG-practice-overview-data-quality-framework.pdf" isExternal>Data quality framework overview</Link>
      <br />
      <Link href="https://www.nzta.govt.nz/assets/Road-Efficiency-Group/docs/practice-overviews/REG-practice-overview-data-quality-dimensions.pdf" isExternal>Data quality dimensions overview</Link>
      <br />
      <Link href="https://www.nzta.govt.nz/assets/Road-Efficiency-Group/docs/RCA-data-quality-reports/asset-management-data-quality-report-score-calculation.pdf" isExternal>Overall Asset Management Score calculation</Link>
    </Text>
  </>,
      source: <Text fontSize="inherit" as="span">Road Efficiency Group Te Ringa Maimoa annual data quality assessment and reporting undertaken within Transport Insights.</Text>,
    },
  ],
};

function DataQualityScore() {
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const rcaId = useRecoilValue(selectedDatabaseIdState);
  const { data, isLoading } = useDQAssetManagement();
  const distinctYears = [...new Set(data?.map((item) => item.Financial_Year))];
  const isRegionNationalOrStateHighways = rcaId >= 1000 || rcaId === 81;
  const isNational = rcaId === 1000;
  let chartFilters = [];
  if (isRegionNationalOrStateHighways) {
    chartFilters = ['Peer'];
  }
  const lockYear = data?.[0]?.LockYear || null;

  // Filter out the National data when National is selected
  const chartData = [
    {
      name: currentDatabase?.name,
      type: 'line',
      data: data?.map((item) => ({ x: item.Financial_Year, y: item.Score })),
      color: CHART_COLOURS.blue,
    },
    ...(!isNational
      ? [
        {
          name: 'National',
          type: 'line',
          data: data?.map((item) => ({ x: item.Financial_Year, y: item.National_Score })),
          color: CHART_COLOURS.grey,
        },
      ]
      : []),
    {
      name: 'Peer Group',
      type: 'line',
      data: data?.map((item) => ({ x: item.Financial_Year, y: item.Peer_Score })),
      color: CHART_COLOURS.black,
    },
  ];

  // Filter out the Peer Group data
  const filteredChartData = filterChartDataByName(
    chartData,
    chartFilters,
  );

  // Extract all values from the chart data for min/max calculations
  const allValues = [...new Set(
    filteredChartData?.flatMap((x) => x.data?.flatMap((y) => (Array.isArray(y.y) ? y.y : [y.y]))),
  )];

  // Set min and max values for y-axis
  const minY = Math.min(...allValues);
  const maxY = Math.max(...allValues);

  return (
    <ReportSection
      title="Annual trend for Score"
      informationSource={informationSource}
      isLoading={isLoading || !data}
      lockYear={lockYear}
    >
      <Chart
        options={
          {
            title: {
              text: 'Data Quality scores',
            },
            chart: {
              animations: {
                enabled: false,
              },
            },
            xaxis: {
              categories: distinctYears,
              labels: {
                offsetY: 5,
                rotate: -45,
                rotateAlways: true,
              },
            },
            yaxis: [
              {
                forceNiceScale: true,
                min: minY,
                max: maxY,
                labels: {
                  formatter(y) {
                    return `${y.toFixed(0)}`;
                  },
                },
              },
            ],
            grid: {
              padding: {
                left: 20,
              },
            },
            dataLabels: {
              enabled: false,
            },
            stroke: {
              curve: 'straight',
              width: distinctYears.length > 1 ? 2 : 0,
            },
            tooltip: {
              shared: true,
              intersect: false,
              y: {
                formatter(value) {
                  return `${value.toFixed(0)}`;
                },
              },
            },
          }
        }
        series={filteredChartData}
        type="line"
        width="280"
        height="280"
      />
      <Text
        align="center"
        mt={4}
        fontSize="xs"
        className="report-source-text"
      >
        Source: Road Efficiency Group
        <br />
        Te Ringa Maimoa Transport Insights
      </Text>
    </ReportSection>
  );
}

export default DataQualityScore;
